import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import LaCantina from "../../assets/restaurant/icon-cantina.png";
import LaTavola from "../../assets/restaurant/icon-tavola.png";
import Brunch from "../../assets/restaurant/icon-brunch.png";
import Grillhouse from "../../assets/restaurant/grillhouse-ejemplo.png";

const CARD_GAP = 16;

const Examples = () => {
	const { t } = useTranslation();
	const primaryColor = "#43bd98";
	const isMobile = useMediaQuery("(max-width: 640px)");
	const trackRef = useRef(null);
	const [active, setActive] = useState(0);
	const webAppTag = t("examples.webAppTag");
	const webTag = t("examples.webTag");

	const stepWidth = () => {
		const track = trackRef.current;
		const first = track?.firstElementChild;
		return first ? first.offsetWidth + CARD_GAP : 0;
	};

	const handleScroll = useCallback(() => {
		const track = trackRef.current;
		const step = stepWidth();
		if (!track || !step) return;
		setActive(Math.round(track.scrollLeft / step));
	}, []);

	const goTo = (index) => {
		const track = trackRef.current;
		if (!track) return;
		track.scrollTo({ left: index * stepWidth(), behavior: "smooth" });
	};

	const examples = [
		{
			name: "La Cantina",
			tag: webAppTag,
			image: LaCantina,
			androidLink:
				"https://play.google.com/store/apps/details?id=com.applaby.lacantina",
			iosLink: "https://apps.apple.com/us/app/la-cantina/id6742712477",
			webLink: "https://www.lacantinarestaurante.com/",
		},
		{
			name: "La Tavola",
			tag: webAppTag,
			image: LaTavola,
			androidLink:
				"https://play.google.com/store/apps/details?id=com.applaby.latavola",
			iosLink: "https://apps.apple.com/us/app/la-tavola/id6744865943",
			webLink: "https://www.latavolaitaliana.es/",
		},
		{
			name: "Brunch & Blossom",
			tag: webAppTag,
			image: Brunch,
			androidLink:
				"https://play.google.com/store/apps/details?id=com.applaby.brunchblossom",
			iosLink: "https://apps.apple.com/us/app/brunch-blossom/id6745568150",
			webLink: "https://www.brunchblossom.com/",
		},
		{
			name: "Grillhouse 27",
			tag: webTag,
			image: Grillhouse,
			webLink: "https://www.grillhouse27.com/",
		},
	];

	return (
		<section
			id="ejemplos"
			style={{
				padding: "5rem 2rem",
				backgroundColor: "#f8fdfb",
				fontFamily: "'Poppins', sans-serif",
				scrollMarginTop: "100px",
			}}
		>
			<motion.div
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
				viewport={{ once: true }}
				style={{
					marginBottom: "3rem",
					maxWidth: "600px",
					margin: "0 auto 3rem",
					textAlign: "center",
				}}
			>
				<p
					style={{
						color: primaryColor,
						fontSize: "0.85rem",
						fontWeight: 600,
						letterSpacing: "1.5px",
						textTransform: "uppercase",
						marginBottom: "0.75rem",
					}}
				>
					{t("examples.subtitle")}
				</p>
				<h2
					style={{
						fontSize: "clamp(1.8rem, 5vw, 2.4rem)",
						color: "#111827",
						fontWeight: 800,
						margin: 0,
						lineHeight: 1.2,
					}}
				>
					{t("examples.title")}
				</h2>
			</motion.div>

			<div
				ref={trackRef}
				onScroll={isMobile ? handleScroll : undefined}
				className={isMobile ? "hide-scrollbar" : undefined}
				style={
					isMobile
						? {
								display: "flex",
								gap: `${CARD_GAP}px`,
								overflowX: "auto",
								scrollSnapType: "x mandatory",
								marginInline: "-2rem",
								paddingInline: "2rem",
								scrollPaddingInline: "2rem",
								WebkitOverflowScrolling: "touch",
						  }
						: {
								display: "grid",
								gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
								gap: "1.25rem",
								maxWidth: "1100px",
								margin: "0 auto",
						  }
				}
			>
				{examples.map((example, index) => (
					<motion.div
						key={example.name}
						initial={{ opacity: 0, y: 40 }}
						animate={isMobile ? { opacity: 1, y: 0 } : undefined}
						whileInView={isMobile ? undefined : { opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: index * 0.1 }}
						viewport={{ once: true, amount: 0.3 }}
						style={{
							display: "flex",
							flexDirection: "column",
							backgroundColor: "#fff",
							borderRadius: "16px",
							overflow: "hidden",
							border: "1px solid #e5e7eb",
							boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
							...(isMobile && {
								flex: "0 0 78%",
								scrollSnapAlign: "start",
							}),
						}}
					>
						<a
							href={example.webLink}
							target="_blank"
							rel="noreferrer"
							style={{
								position: "relative",
								height: "160px",
								background: "#f3f4f6",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								padding: "2rem",
								borderBottom: "1px solid #f3f4f6",
							}}
						>
							<img
								src={example.image}
								alt={example.name}
								style={{
									maxHeight: "100%",
									maxWidth: "100%",
									objectFit: "contain",
								}}
							/>
						</a>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								flex: 1,
								padding: "1.25rem",
							}}
						>
							<div style={{ marginBottom: "1.25rem" }}>
								<p
									style={{
										fontSize: "11px",
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										color: "#6b7280",
										marginBottom: "6px",
									}}
								>
									{example.tag}
								</p>
								<h3
									style={{
										fontSize: "1rem",
										fontWeight: 600,
										color: "#111827",
										margin: 0,
									}}
								>
									{example.name}
								</h3>
							</div>

							<div style={{ marginTop: "auto" }}>
								<a
									href={example.webLink}
									target="_blank"
									rel="noreferrer"
									style={{
										display: "inline-flex",
										width: "100%",
										alignItems: "center",
										justifyContent: "center",
										gap: "8px",
										background: primaryColor,
										color: "#fff",
										padding: "10px 16px",
										borderRadius: "12px",
										fontSize: "0.875rem",
										fontWeight: 600,
										textDecoration: "none",
										boxSizing: "border-box",
									}}
								>
									{t("examples.tryDemo")} <ArrowRight size={16} />
								</a>

								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "10px",
										marginTop: "12px",
										fontSize: "0.75rem",
										color: "#6b7280",
										minHeight: "1.25rem",
									}}
								>
									{example.iosLink && (
										<a
											href={example.iosLink}
											target="_blank"
											rel="noreferrer"
											style={{ color: "#6b7280", textDecoration: "none" }}
										>
											{t("examples.ios")}
										</a>
									)}
									{example.iosLink && example.androidLink && (
										<span style={{ color: "#d1d5db" }}>·</span>
									)}
									{example.androidLink && (
										<a
											href={example.androidLink}
											target="_blank"
											rel="noreferrer"
											style={{ color: "#6b7280", textDecoration: "none" }}
										>
											{t("examples.android")}
										</a>
									)}
								</div>
							</div>
						</div>
					</motion.div>
				))}
			</div>

			{isMobile && (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						gap: "8px",
						marginTop: "1.25rem",
					}}
				>
					{examples.map((example, index) => (
						<button
							key={example.name}
							type="button"
							onClick={() => goTo(index)}
							aria-label={example.name}
							aria-current={index === active}
							style={{
								width: "24px",
								height: "24px",
								padding: 0,
								border: "none",
								background: "none",
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<span
								style={{
									display: "block",
									width: index === active ? "24px" : "8px",
									height: "8px",
									borderRadius: "4px",
									background: index === active ? primaryColor : "#d1d5db",
									transition: "width 0.25s ease, background 0.25s ease",
								}}
							/>
						</button>
					))}
				</div>
			)}
		</section>
	);
};

export default Examples;
